// Entreprenörer och Bas-U per etapp – ett bygge kan ha flera samtidiga
// entreprenörer och byte av Bas-U mellan etapper (AFS 2023:3 kap. 2).
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HardHat, Plus, Trash2 } from "lucide-react";

interface Props {
  projectId: string;
  stageId: string;
  canEdit?: boolean;
}

interface Row {
  id: string;
  company: string;
  contact_name: string | null;
  contact_email: string | null;
  scope: string | null;
  is_bas_u: boolean;
}

const StageContractors: React.FC<Props> = ({ projectId, stageId, canEdit = true }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("stage_contractors")
      .select("id, company, contact_name, contact_email, scope, is_bas_u")
      .eq("stage_id", stageId)
      .order("sort_order", { ascending: true });
    if (error) toast.error("Kunde inte läsa entreprenörerna");
    setRows((data as Row[] | null) ?? []);
    setLoading(false);
  }, [stageId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("stage_contractors").insert({
      project_id: projectId,
      stage_id: stageId,
      company: "",
      sort_order: rows.length,
      created_by: auth.user?.id ?? null,
    });
    if (error) return toast.error("Kunde inte lägga till entreprenören");
    load();
  };

  const patch = async (id: string, p: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...p } : r)));
    const { error } = await supabase.from("stage_contractors").update(p).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    await supabase.from("stage_contractors").delete().eq("id", id);
  };

  const basUCount = rows.filter((r) => r.is_bas_u).length;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-sm">
            <HardHat className="h-4 w-4 text-primary" />
            Entreprenörer i etappen
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Markera vem som är Bas-U i etappen. Flera entreprenörer kan arbeta samtidigt.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={basUCount === 0 ? "destructive" : "secondary"}>
            {basUCount === 0 ? "Bas-U saknas" : `${basUCount} Bas-U`}
          </Badge>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={add}>
              <Plus className="mr-1 h-4 w-4" /> Lägg till
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <p className="text-sm text-muted-foreground">Laddar…</p>}
        {!loading && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">Inga entreprenörer registrerade.</p>
        )}
        {rows.map((r) => (
          <div key={r.id} className="grid gap-3 rounded-md border p-3 sm:grid-cols-2">
            <div>
              <Label>Företag</Label>
              <Input
                value={r.company}
                disabled={!canEdit}
                onChange={(e) => patch(r.id, { company: e.target.value })}
              />
            </div>
            <div>
              <Label>Kontaktperson</Label>
              <Input
                value={r.contact_name ?? ""}
                disabled={!canEdit}
                onChange={(e) => patch(r.id, { contact_name: e.target.value })}
              />
            </div>
            <div>
              <Label>E-post</Label>
              <Input
                value={r.contact_email ?? ""}
                disabled={!canEdit}
                onChange={(e) => patch(r.id, { contact_email: e.target.value })}
              />
            </div>
            <div>
              <Label>Omfattning</Label>
              <Input
                value={r.scope ?? ""}
                disabled={!canEdit}
                onChange={(e) => patch(r.id, { scope: e.target.value })}
                placeholder="T.ex. stomme plan 1–4"
              />
            </div>
            <div className="flex items-center justify-between sm:col-span-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={r.is_bas_u}
                  disabled={!canEdit}
                  onCheckedChange={(v) => patch(r.id, { is_bas_u: v })}
                />
                <span className="text-sm">Är Bas-U för etappen</span>
              </div>
              {canEdit && (
                <Button size="sm" variant="ghost" onClick={() => remove(r.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default StageContractors;
